import { useMemo } from "react";
import { Loader2 } from "lucide-react";
import type { DataRecord } from "../types";

interface SemrushSiteAuditCardsProps {
  siteHealthTrend?: DataRecord[];
  siteAuditIssues?: DataRecord[];
  siteAuditPages?: DataRecord[];
  isUpdating?: boolean;
}

export function SemrushSiteAuditCards({
  siteHealthTrend = [],
  siteAuditIssues = [],
  siteAuditPages = [],
  isUpdating = false,
}: SemrushSiteAuditCardsProps) {
  // Tomar el registro más reciente cronológicamente
  const latestHealth = useMemo(() => {
    if (!siteHealthTrend.length) return null;
    const sorted = [...siteHealthTrend].sort((a, b) =>
      String(b.snapshot_date || "").localeCompare(String(a.snapshot_date || ""))
    );
    return sorted[0];
  }, [siteHealthTrend]);

  const healthScore = latestHealth?.health_score != null ? Number(latestHealth.health_score) : null;
  const pagesCrawled = Number(latestHealth?.pages_crawled ?? (siteAuditPages.length || 0));
  const errorsCount = Number(latestHealth?.errors_failed_checks ?? latestHealth?.errors_count_agregado ?? 0);
  const warningsCount = Number(latestHealth?.warnings_failed_checks ?? latestHealth?.warnings_count_agregado ?? 0);
  const noticesCount = Number(latestHealth?.notices_failed_checks ?? latestHealth?.notices_count ?? 0);

  // Desglose dinámico de páginas rastreadas según el dataset real del cliente
  const crawledBreakdown = useMemo(() => {
    if (!siteAuditPages.length) {
      return { correctas: 0, rotas: 0, conProblemas: 0, redirigidas: 0, bloqueadas: 0, total: pagesCrawled || 1 };
    }
    let rotas = 0;
    let redir = 0;
    let conProb = 0;
    let correctas = 0;
    siteAuditPages.forEach((p) => {
      const code = Number(p.http_status_code || 200);
      const issues = Number(p.issues_count || 0);
      if (code >= 400) rotas++;
      else if (code >= 300) redir++;
      else if (issues > 0) conProb++;
      else correctas++;
    });
    const total = siteAuditPages.length;
    return {
      correctas,
      rotas,
      conProblemas: conProb,
      redirigidas: redir,
      bloqueadas: 0,
      total: total || 1,
    };
  }, [siteAuditPages, pagesCrawled]);

  // Desglose de códigos HTTP y arquitectura desde siteAuditPages reales
  const httpStatusBreakdown = useMemo(() => {
    if (!siteAuditPages.length) {
      return { ok200: 0, redir3xx: 0, err4xx: 0, err5xx: 0, inSitemap: 0, total: 0 };
    }
    let ok200 = 0;
    let redir3xx = 0;
    let err4xx = 0;
    let err5xx = 0;
    let inSitemap = 0;
    siteAuditPages.forEach((p) => {
      const code = Number(p.http_status_code || 200);
      if (code >= 200 && code < 300) ok200++;
      else if (code >= 300 && code < 400) redir3xx++;
      else if (code >= 400 && code < 500) err4xx++;
      else if (code >= 500) err5xx++;

      if (p.in_sitemap === true || p.in_sitemap === 1 || String(p.in_sitemap).toLowerCase() === "true") {
        inSitemap++;
      }
    });
    return {
      ok200,
      redir3xx,
      err4xx,
      err5xx,
      inSitemap,
      total: siteAuditPages.length,
    };
  }, [siteAuditPages]);

  const totalIssues = errorsCount + warningsCount + noticesCount;

  // Top issues detectados
  const topErrors = useMemo(() => {
    return siteAuditIssues
      .filter((i) => String(i.issue_type).toUpperCase() === "ERROR")
      .slice(0, 3);
  }, [siteAuditIssues]);

  const topWarnings = useMemo(() => {
    return siteAuditIssues
      .filter((i) => String(i.issue_type).toUpperCase() === "WARNING")
      .slice(0, 4);
  }, [siteAuditIssues]);

  // Extraer la fecha del último import de CSV (Issues o Pages de Site Audit)
  const lastCsvDate = useMemo(() => {
    const dates = [
      ...siteAuditIssues.map((i) => String(i.snapshot_date || "").slice(0, 10)),
      ...siteAuditPages.map((p) => String(p.snapshot_date || "").slice(0, 10)),
    ].filter(Boolean);
    if (!dates.length) return null;
    return dates.sort().reverse()[0];
  }, [siteAuditIssues, siteAuditPages]);

  // Desglose real de páginas existentes
  const hasPageBreakdown = Boolean(
    crawledBreakdown.correctas > 0 ||
    crawledBreakdown.rotas > 0 ||
    crawledBreakdown.conProblemas > 0 ||
    crawledBreakdown.redirigidas > 0 ||
    httpStatusBreakdown.ok200 > 0 ||
    httpStatusBreakdown.redir3xx > 0 ||
    httpStatusBreakdown.err4xx > 0 ||
    httpStatusBreakdown.err5xx > 0
  );

  // Hallazgos reales
  const hasIssues = Boolean(
    siteAuditIssues.length > 0 ||
    totalIssues > 0 ||
    errorsCount > 0 ||
    warningsCount > 0 ||
    noticesCount > 0
  );

  // Health score real
  const hasPositiveHealth = Boolean(healthScore !== null && healthScore > 0);

  // Ocultar dinámicamente si está en 0% de salud, con 0 hallazgos y sin desglose de páginas
  const hasValidAudit = hasPositiveHealth || hasIssues || hasPageBreakdown;

  if (!hasValidAudit) {
    return null;
  }

  return (
    <div className="semrush-harmonized-card">
      <div className="semrush-harmonized-header">
        <div className="semrush-header-title">
          <span className="semrush-badge-pill">SEMRUSH SITE AUDIT</span>
          <h3>Salud Técnica y Rastreo Web</h3>
          {isUpdating && (
            <span
              style={{
                fontSize: "11px",
                padding: "2px 8px",
                borderRadius: "12px",
                background: "rgba(59, 130, 246, 0.15)",
                color: "#60a5fa",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <Loader2 className="spinner animate-spin" size={12} />
              <span>Actualizando…</span>
            </span>
          )}
          {lastCsvDate && !isUpdating && (
            <span
              className="semrush-csv-badge"
              style={{
                fontSize: "11px",
                padding: "2px 8px",
                borderRadius: "12px",
                background: "rgba(110, 140, 255, 0.12)",
                color: "#7d96ff",
                border: "1px solid rgba(110, 140, 255, 0.25)",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
              title="Última fecha de importación de CSV para Site Audit de este cliente"
            >
              CSV Site Health: {lastCsvDate}
            </span>
          )}
        </div>
        <div className="semrush-header-date">
          Snapshot activo: <strong>{latestHealth?.snapshot_date ? String(latestHealth.snapshot_date) : (lastCsvDate ?? "Sin auditoría cargada")}</strong>
        </div>
      </div>

      {/* Grid de 4 tarjetas ejecutivas estilo Semrush con datos 100% reales */}
      <div className="semrush-grid-4" style={{ opacity: isUpdating ? 0.6 : 1, transition: "opacity 0.2s ease" }}>
        {/* Card 1: Site Health */}
        <div className="semrush-kpi-box">
          <div className="semrush-box-label">Site Health</div>
          <div className="semrush-gauge-wrap">
            <div
              className="semrush-gauge-val"
              style={{ color: healthScore != null ? (healthScore >= 80 ? "#10b981" : "#f59e0b") : "var(--muted, #8892b0)" }}
            >
              {healthScore != null ? `${healthScore}%` : "—"}
            </div>
            <div className="semrush-gauge-sub">
              {healthScore != null
                ? healthScore >= 80
                  ? "Salud técnica buena"
                  : healthScore >= 60
                  ? "Atención requerida"
                  : "Salud crítica"
                : "Sin auditoría"}
            </div>
          </div>
          <div className="semrush-benchmark-list">
            <div className="semrush-benchmark-item">
              <span><i className="dot dot-red" /> Errores críticos</span>
              <strong>{errorsCount}</strong>
            </div>
            <div className="semrush-benchmark-item">
              <span><i className="dot dot-yellow" /> Advertencias</span>
              <strong>{warningsCount}</strong>
            </div>
            <div className="semrush-benchmark-item">
              <span><i className="dot dot-blue" /> Avisos</span>
              <strong>{noticesCount}</strong>
            </div>
          </div>
        </div>

        {/* Card 2: Páginas rastreadas */}
        <div className="semrush-kpi-box">
          <div className="semrush-box-label">Páginas rastreadas</div>
          <div className="semrush-big-stat">
            <span className="stat-number">{pagesCrawled}</span>
            <span className="stat-sub">{pagesCrawled > 0 ? "Páginas evaluadas" : "Sin rastreo"}</span>
          </div>
          {/* Barra segmentada */}
          <div className="semrush-segment-bar" title="Distribución de URLs">
            <div
              style={{ width: `${(crawledBreakdown.correctas / crawledBreakdown.total) * 100}%`, background: "#10b981" }}
              title={`Correctas: ${crawledBreakdown.correctas}`}
            />
            <div
              style={{ width: `${(crawledBreakdown.conProblemas / crawledBreakdown.total) * 100}%`, background: "#f59e0b" }}
              title={`Con problemas: ${crawledBreakdown.conProblemas}`}
            />
            <div
              style={{ width: `${(crawledBreakdown.redirigidas / crawledBreakdown.total) * 100}%`, background: "#6366f1" }}
              title={`Redirigidas: ${crawledBreakdown.redirigidas}`}
            />
            <div
              style={{ width: `${(crawledBreakdown.rotas / crawledBreakdown.total) * 100}%`, background: "#ef4444" }}
              title={`Rotas: ${crawledBreakdown.rotas}`}
            />
          </div>
          <div className="semrush-page-breakdown">
            <div className="breakdown-row">
              <span><i className="dot dot-green" /> Correctas</span>
              <strong>{crawledBreakdown.correctas}</strong>
            </div>
            <div className="breakdown-row">
              <span><i className="dot dot-yellow" /> Con problemas</span>
              <strong>{crawledBreakdown.conProblemas}</strong>
            </div>
            <div className="breakdown-row">
              <span><i className="dot dot-indigo" /> Redirigidas</span>
              <strong>{crawledBreakdown.redirigidas}</strong>
            </div>
            <div className="breakdown-row">
              <span><i className="dot dot-red" /> Rotas</span>
              <strong>{crawledBreakdown.rotas}</strong>
            </div>
          </div>
        </div>

        {/* Card 3: Distribución de Hallazgos */}
        <div className="semrush-kpi-box">
          <div className="semrush-box-label">Distribución de Hallazgos</div>
          <div className="semrush-big-stat">
            <span className="stat-number">{totalIssues}</span>
            <span className="stat-sub">
              {totalIssues > 0 ? `${errorsCount} errores · ${warningsCount} advertencias` : "Sin hallazgos reportados"}
            </span>
          </div>
          {/* Barra de severidad */}
          <div className="semrush-segment-bar" title="Distribución de Hallazgos">
            <div
              style={{ width: `${totalIssues > 0 ? (errorsCount / totalIssues) * 100 : 0}%`, background: "#ef4444" }}
              title={`Errores: ${errorsCount}`}
            />
            <div
              style={{ width: `${totalIssues > 0 ? (warningsCount / totalIssues) * 100 : 0}%`, background: "#f59e0b" }}
              title={`Advertencias: ${warningsCount}`}
            />
            <div
              style={{ width: `${totalIssues > 0 ? (noticesCount / totalIssues) * 100 : 0}%`, background: "#3b82f6" }}
              title={`Avisos: ${noticesCount}`}
            />
          </div>
          <div className="semrush-page-breakdown">
            <div className="breakdown-row">
              <span><i className="dot dot-red" /> Errores críticos</span>
              <strong>{errorsCount}</strong>
            </div>
            <div className="breakdown-row">
              <span><i className="dot dot-yellow" /> Advertencias</span>
              <strong>{warningsCount}</strong>
            </div>
            <div className="breakdown-row">
              <span><i className="dot dot-blue" /> Avisos / Oportunidades</span>
              <strong>{noticesCount}</strong>
            </div>
          </div>
        </div>

        {/* Card 4: Códigos HTTP y Rastreo */}
        <div className="semrush-kpi-box">
          <div className="semrush-box-label">Códigos HTTP y Rastreo</div>
          <div className="semrush-big-stat">
            <span className="stat-number">{httpStatusBreakdown.total > 0 ? httpStatusBreakdown.total : pagesCrawled}</span>
            <span className="stat-sub">
              {httpStatusBreakdown.total > 0 ? "Páginas auditadas" : "Sin desglose de páginas"}
            </span>
          </div>
          <div className="semrush-page-breakdown">
            <div className="breakdown-row">
              <span><i className="dot dot-green" /> 200 OK</span>
              <strong>{httpStatusBreakdown.ok200}</strong>
            </div>
            <div className="breakdown-row">
              <span><i className="dot dot-indigo" /> 3xx Redirigidas</span>
              <strong>{httpStatusBreakdown.redir3xx}</strong>
            </div>
            <div className="breakdown-row">
              <span><i className="dot dot-red" /> 4xx / 5xx Errores</span>
              <strong>{httpStatusBreakdown.err4xx + httpStatusBreakdown.err5xx}</strong>
            </div>
            <div className="breakdown-row">
              <span><i className="dot dot-blue" /> En Sitemap</span>
              <strong>{httpStatusBreakdown.inSitemap}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Fila de Issues Críticos y Advertencias (Estilo Semrush) */}
      <div className="semrush-issues-preview-row">
        <div className="semrush-issues-col error-col">
          <div className="issues-col-header">
            <span className="count-pill error-pill">{errorsCount}</span>
            <h4>Errores Técnicos Críticos</h4>
          </div>
          <ul className="issues-col-list">
            {topErrors.length ? (
              topErrors.map((err, idx) => (
                <li key={idx} className="issue-item">
                  <span className="issue-icon error">⚡</span>
                  <span className="issue-desc">{String(err.issue_name || "Error detectado")}</span>
                  <span className="issue-cnt">{Number(err.failed_checks || 0)} páginas</span>
                </li>
              ))
            ) : (
              <li className="issue-item">
                <span className="issue-icon info">✓</span>
                <span className="issue-desc" style={{ color: "var(--muted, #8892b0)" }}>Sin errores críticos reportados</span>
              </li>
            )}
          </ul>
        </div>

        <div className="semrush-issues-col warning-col">
          <div className="issues-col-header">
            <span className="count-pill warning-pill">{warningsCount}</span>
            <h4>Advertencias y Optimización</h4>
          </div>
          <ul className="issues-col-list">
            {topWarnings.length ? (
              topWarnings.map((warn, idx) => (
                <li key={idx} className="issue-item">
                  <span className="issue-icon warning">⚠️</span>
                  <span className="issue-desc">{String(warn.issue_name || "Advertencia")}</span>
                  <span className="issue-cnt">{Number(warn.failed_checks || 0)} páginas</span>
                </li>
              ))
            ) : (
              <li className="issue-item">
                <span className="issue-icon info">✓</span>
                <span className="issue-desc" style={{ color: "var(--muted, #8892b0)" }}>Sin advertencias pendientes</span>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
